/**
 * A form which is sent once: the review of the checkout, whose button makes the order
 * (checkout/review.php marks it with data-eg-submit-once). The first submit marks the form busy
 * (aria-busy) and disables its submit buttons, so a double click, or a click while the answer is
 * on its way, sends nothing a second time. The server takes the second submit too (the cart is
 * history once the order is made), this only keeps the buyer from sending it.
 *
 * - The buttons are disabled after the browser took the values of the form: a disabled button
 *   sends no name, and the order needs the name of its button.
 * - A submit which another script or the browser stopped (a check which failed) leaves the form
 *   as it is.
 * - A page the back button brings back from the cache of the browser is the form as it was left:
 *   it can be sent again.
 */
export const SUBMIT_ONCE_SELECTOR = 'form[data-eg-submit-once]';

const bound = new WeakSet();

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLElement[]} the buttons which send the form, those outside it (form attribute) too
 */
function submitButtonsOf(form) {
    return Array.from(form.elements).filter((element) => element.type === 'submit');
}

/**
 * Makes a form sendable again: not busy, its buttons enabled.
 *
 * @param {HTMLFormElement} form
 */
export function release(form) {
    form.removeAttribute('aria-busy');
    submitButtonsOf(form).forEach((button) => {
        button.disabled = false;
    });
}

/**
 * @param {Document} root the document whose forms are watched
 * @param {Window} view the window of the document, whose page cache brings a page back
 */
export function bindSubmitOnce(root = document, view = window) {
    if (bound.has(root)) {
        return;
    }
    bound.add(root);

    root.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches(SUBMIT_ONCE_SELECTOR)) {
            return;
        }
        if (form.getAttribute('aria-busy') === 'true') {
            // sent already: the answer is on its way
            event.preventDefault();
            return;
        }
        if (event.defaultPrevented) {
            return;
        }
        form.setAttribute('aria-busy', 'true');
        setTimeout(() => {
            submitButtonsOf(form).forEach((button) => {
                button.disabled = true;
            });
        }, 0);
    });

    view.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            root.querySelectorAll(SUBMIT_ONCE_SELECTOR).forEach((form) => release(form));
        }
    });
}

bindSubmitOnce(document, window);
